import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { generateTeacherContent, type AssistantMode } from "@/lib/teacher-assistant.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sparkles, BookOpen, ClipboardList, ClipboardCheck, MessageSquare, Lightbulb, Save, Copy, Loader2,
} from "lucide-react";

export const Route = createFileRoute("/teacher/assistant")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "AI Assistant · MAKAI Teachers" }] }),
  component: Page,
});

const MODES: { id: AssistantMode; label: string; desc: string; icon: typeof Sparkles }[] = [
  { id: "lesson_plan", label: "Lesson plan", desc: "NCDC-aligned, phased plan", icon: BookOpen },
  { id: "test", label: "Test", desc: "MCQs + structured questions", icon: ClipboardList },
  { id: "marking_scheme", label: "Marking scheme", desc: "Model answers + criteria", icon: ClipboardCheck },
  { id: "report_comments", label: "Report comments", desc: "End-of-term narrative", icon: MessageSquare },
  { id: "classroom_activities", label: "Classroom activities", desc: "Low-cost, ready to run", icon: Lightbulb },
];

function Page() {
  const ctx = useTeacherContext();
  const generate = useServerFn(generateTeacherContent);
  const [mode, setMode] = useState<AssistantMode>("lesson_plan");
  const [subject, setSubject] = useState("");
  const [className, setClassName] = useState("");
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [learnerName, setLearnerName] = useState("");
  const [tone, setTone] = useState("encouraging");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);

  async function onGenerate() {
    setBusy(true); setOutput("");
    try {
      const res = await generate({ data: { mode, subject, className, topic, notes, learnerName, tone } });
      setOutput(res.content);
      // Auto-save to teaching_resources
      if (res.content && ctx.staffId && ctx.schoolId) {
        const title = `[${mode.replace(/_/g, " ")}] ${topic || subject || learnerName || new Date().toLocaleString()}`;
        const url = `data:text/markdown;charset=utf-8,${encodeURIComponent(res.content)}`;
        await supabase.from("teaching_resources").insert({
          school_id: ctx.schoolId, teacher_id: ctx.staffId,
          title: title.slice(0, 200), description: `Auto-saved AI ${mode}`,
          kind: "note", url, visibility: "private",
        });
        toast.success("Saved to your private resources");
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Generation failed");
    } finally { setBusy(false); }
  }

  return (
    <AppShell role="teacher" title="AI Assistant" subtitle="Lesson plans, tests, marking schemes, report comments & activities">
      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Choose task</div>
            <div className="grid gap-2">
              {MODES.map((m) => {
                const active = m.id === mode;
                return (
                  <button key={m.id} onClick={() => setMode(m.id)}
                    className={`grid grid-cols-[auto_1fr] items-center gap-3 rounded-xl border p-3 text-left transition ${active ? "border-primary bg-primary-soft" : "border-border bg-background/60 hover:border-primary/40"}`}>
                    <div className={`grid h-9 w-9 place-items-center rounded-lg ${active ? "bg-primary text-primary-foreground" : "bg-primary-soft text-primary"}`}>
                      <m.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{m.label}</div>
                      <div className="text-xs text-muted-foreground">{m.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 shadow-soft space-y-3">
            <div className="text-sm font-semibold">Context</div>
            {mode === "report_comments" ? (
              <>
                <div><Label>Learner name</Label><Input value={learnerName} onChange={(e) => setLearnerName(e.target.value)} placeholder="e.g. Achen Sarah" /></div>
                <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="P5 / S2" /></div>
                <div><Label>Subject focus</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" /></div>
                <div>
                  <Label>Tone</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="encouraging">Encouraging</SelectItem>
                      <SelectItem value="formal">Formal</SelectItem>
                      <SelectItem value="firm but kind">Firm but kind</SelectItem>
                      <SelectItem value="warm">Warm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Mathematics" /></div>
                <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="P5 / S2" /></div>
                <div><Label>Topic</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Fractions" /></div>
              </>
            )}
            <div>
              <Label>{mode === "marking_scheme" ? "Paste test/questions" : "Notes (optional)"}</Label>
              <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder={mode === "marking_scheme" ? "Paste the questions here…" : "Any specifics, learner level, etc."} />
            </div>
            <Button onClick={onGenerate} disabled={busy} className="w-full">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Generate
            </Button>
          </div>
        </aside>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-semibold text-muted-foreground">AI output</div>
            {output && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(output); toast.success("Copied"); }}>
                  <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!ctx.staffId || !ctx.schoolId) return;
                  const url = `data:text/markdown;charset=utf-8,${encodeURIComponent(output)}`;
                  await supabase.from("teaching_resources").insert({
                    school_id: ctx.schoolId, teacher_id: ctx.staffId,
                    title: `[${mode}] ${topic || subject || "Untitled"}`.slice(0, 200),
                    kind: "note", url, visibility: "class",
                  });
                  toast.success("Shared with class");
                }}>
                  <Save className="mr-1 h-3.5 w-3.5" /> Save to class
                </Button>
              </div>
            )}
          </div>
          {busy && !output && (
            <div className="grid h-64 place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          )}
          {!busy && !output && (
            <div className="grid h-64 place-items-center rounded-xl border border-dashed border-border text-center text-sm text-muted-foreground">
              Fill in the context on the left and tap <b className="mx-1">Generate</b> to begin.
            </div>
          )}
          {output && (
            <article className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>{output}</ReactMarkdown>
            </article>
          )}
        </section>
      </div>
    </AppShell>
  );
}
